import type {
  GradientValue,
  SolidOrGradient,
  SolidValue,
} from "replo-runtime/shared/types";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import type { SavedStyleColorAttributes } from "schemas/generated/savedStyles";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import ColorPickerWrapper from "@v1/components/common/designSystem/color/ColorPickerWrapper";
import GradientPicker from "@v1/components/common/designSystem/color/GradientPicker";
import SolidColorPicker from "@v1/components/common/designSystem/color/SolidColorPicker";
import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import {
  getColorBadgeValue,
  hasDynamicData,
} from "@v1/components/editor/page/element-editor/components/modifiers/utils";
import useGetDeletedSavedStyleValueIfNeeded from "@v1/hooks/designLibrary/useGetDeletedSavedStyleValueIfNeeded";
import useShopStyles from "@v1/hooks/designLibrary/useGetDesignLibrarySavedStyles";
import {
  selectColorGradientDesignLibrary,
  selectDraftComponentId,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import {
  getAutoCompletedColor,
  getFormattedColor,
  getFormattedColorWithoutOpacity,
  getHex8Color,
  isGradientColor,
  isSolidColor,
} from "@v2/utils/colors";
import isEqual from "lodash-es/isEqual";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { getSavedStyleValue } from "replo-runtime/shared/savedStyles";
import { isDynamicDesignLibraryValue } from "replo-runtime/shared/utils/designLibrary";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import { useControllableState } from "replo-utils/react/use-controllable-state";
import { v4 as uuidv4 } from "uuid";

import { DynamicDataSelector } from "../../DynamicDataSelector";

type Props = {
  value: SolidOrGradient | ReploMixedStyleValue | null;
  onPreviewChange?(value: SolidOrGradient | null): void;
  onChange(value: SolidOrGradient | string | null): void;
  onRemove?(): void;
  onDragStart?(e: React.MouseEvent): void;
  onDragEnd?(e: React.MouseEvent): void;
  popoverTitle?: string;
  isDisabled?: boolean;
  allowColorPickerPopover?: boolean;
  popoverSideOffset?: number;
  isPopoverOpen?: boolean;
  onOpenPopoverChange?(isOpen: boolean): void;
  onSelectSavedStyle?(value: string): void;
  showSavedStyles?: boolean;
};

const GradientSelector: React.FC<React.PropsWithChildren<Props>> = ({
  value,
  onChange,
  onRemove,
  onPreviewChange,
  onDragEnd,
  onDragStart,
  popoverTitle = "Color",
  isDisabled,
  allowColorPickerPopover = true,
  popoverSideOffset,
  isPopoverOpen: controlledIsPopoverOpen,
  onOpenPopoverChange,
  onSelectSavedStyle,
  showSavedStyles,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useControllableState(
    controlledIsPopoverOpen,
    false,
    onOpenPopoverChange,
  );

  const { textShopStyles: textSavedStyles, colorShopStyles: colorSavedStyles } =
    useShopStyles();
  const colorGradientDesignLibrary = useEditorSelector(
    selectColorGradientDesignLibrary,
  );
  const hasMixedStyleValue = isMixedStyleValue(value);
  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change to it.
  const deletedSavedStyleValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleColorAttributes>(
      String(value) ?? null,
    )?.color;
  // NOTE (Fran 2024-11-29): We know that if the saved style value is undefined or null, the saved style
  // is not deleted, and we need to show it as it is.
  const wasSavedStyleDeleted = Boolean(deletedSavedStyleValue);
  const isGradientFromDesignLibrary =
    !hasMixedStyleValue &&
    value?.type === "gradient" &&
    !isMixedStyleValue(colorGradientDesignLibrary) &&
    Boolean(colorGradientDesignLibrary);
  const isColorFromDesignLibrary =
    !hasMixedStyleValue &&
    value?.type === "solid" &&
    isDynamicDesignLibraryValue(value.color ?? null);
  const isDesignLibraryValue =
    isColorFromDesignLibrary || isGradientFromDesignLibrary;

  const isSolid = isSolidColor(value);
  const [toggleGroupValue, setToggleGroupValue] = React.useState(
    isSolid || isDesignLibraryValue ? "solid" : "linear",
  );
  const [lastValidColorValue, setLastValidColorValue] = React.useState(
    !hasMixedStyleValue ? value : null,
  );

  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const dynamicData = hasDynamicData(draftComponentId);

  const handleChange = React.useCallback(
    (newValue: SolidOrGradient | null) => {
      // NOTE (Cole 2025-01-27): This is a complicated comparison block because we need to compare the gradient stops without the IDs since they're not relevant for equality. We also need to compare the colors case-insensitively.
      const areEqual = (() => {
        if (!newValue || !lastCommittedColorRef.current) {
          return false;
        }
        if (newValue.type !== lastCommittedColorRef.current.type) {
          return false;
        }

        if (
          newValue.type === "gradient" &&
          lastCommittedColorRef.current.type === "gradient"
        ) {
          const newStops = newValue.gradient.stops.map(({ id, ...rest }) => ({
            ...rest,
            color: rest.color?.toLowerCase() ?? "",
          }));
          const oldStops = lastCommittedColorRef.current.gradient.stops.map(
            ({ id, ...rest }) => ({
              ...rest,
              color: rest.color?.toLowerCase() ?? "",
            }),
          );
          return (
            newValue.gradient.tilt ===
              lastCommittedColorRef.current.gradient.tilt &&
            isEqual(newStops, oldStops)
          );
        }

        // For solid colors, also compare case-insensitively
        if (
          newValue.type === "solid" &&
          lastCommittedColorRef.current.type === "solid"
        ) {
          const newColor = newValue.color?.toLowerCase() ?? "";
          const oldColor =
            lastCommittedColorRef.current.color?.toLowerCase() ?? "";
          return newColor === oldColor;
        }

        return false;
      })();

      if (!areEqual) {
        lastCommittedColorRef.current = newValue;
        onChange(newValue);
      }
    },
    [onChange],
  );

  const lastCommittedColorRef = React.useRef<SolidOrGradient | null>(
    (!isMixedStyleValue(value) ? value : null) as SolidOrGradient | null,
  );

  const getInputValue = () => {
    if (!value) {
      return "";
    }

    if (hasMixedStyleValue) {
      return "Mixed";
    }

    if (
      !wasSavedStyleDeleted &&
      (isColorFromDesignLibrary || isGradientFromDesignLibrary)
    ) {
      // NOTE (Fran 2024-11-27): If the color is from the design library, we need to
      // show the saved style value reference instead of the value itself.
      const savedStyleValueReference = isGradientFromDesignLibrary
        ? colorGradientDesignLibrary!
        : (value as SolidValue).color ?? "";
      const savedStyle = getSavedStyleValue(
        // NOTE (Sebas, 2024-11-14): We look for the color in both text and color type saved
        // styles. This is because when we select a text style with a font color we
        // want to show the color in the value indicator.
        [...textSavedStyles, ...colorSavedStyles],
        savedStyleValueReference,
      );

      return savedStyle?.type === "text"
        ? `${savedStyle?.attributes.color} (${savedStyle?.name})`
        : savedStyle?.name;
    }

    return value?.type === "gradient"
      ? "Gradient"
      : getFormattedColorWithoutOpacity(value?.color ?? "") ?? "";
  };
  const valueText = getInputValue();
  const [inputValue, setInputValue] = useOverridableState(valueText);
  const isInputReadOnly =
    hasMixedStyleValue || value?.type === "gradient" || isDesignLibraryValue;

  const handleColorChange = () => {
    // NOTE (Fran 2025-01-24): We don't need to update anything if the input is read only.
    // Meaning if the Color Applied is solid.
    // Mixed Values, Saved Styles and Gradient Colors are handled diferently.
    if (isInputReadOnly) {
      return;
    }

    const color = getAutoCompletedColor(inputValue);
    if (color) {
      // Always convert to uppercase for display in the input field
      const hexColor = getFormattedColor(color);
      const newValue = { type: "solid", color: hexColor } as const;

      setLastValidColorValue(newValue);
      handleChange(newValue);
    } else if (lastValidColorValue) {
      handleChange(lastValidColorValue);
    }
  };

  const handleColorRemove = () => {
    if (onRemove) {
      onRemove();
    } else {
      handleChange({ type: "solid", color: "" });
    }
  };

  const getBadgeValue = () => {
    if (!value || hasMixedStyleValue) {
      return "";
    }

    if (
      !wasSavedStyleDeleted &&
      (isColorFromDesignLibrary || isGradientFromDesignLibrary)
    ) {
      // NOTE (Fran 2024-11-27): If the color is from the design library, we need to
      // show the saved style value reference instead of the value itself.
      const savedStyle = getSavedStyleValue(
        // NOTE (Sebas, 2024-11-14): We look for the color in both text and color type saved
        // styles. This is because when we select a text style with a font color we
        // want to show the color in the value indicator.
        [...textSavedStyles, ...colorSavedStyles],
        isGradientFromDesignLibrary
          ? colorGradientDesignLibrary!
          : (value as SolidValue).color ?? "",
      );

      return savedStyle?.attributes.color ?? "";
    }

    return getColorBadgeValue(value);
  };
  const badgeValue = getBadgeValue();

  if (isDisabled) {
    return (
      <div className="flex flex-grow flex-row justify-items-start">
        <DeprecatedInput
          value={valueText}
          startEnhancer={
            <Badge
              type={!isMixedStyleValue(value) ? "color" : "unknown"}
              isFilled
              backgroundColor={badgeValue}
            />
          }
          isDisabled
          endEnhancer={() => {
            return (
              handleColorRemove &&
              valueText && (
                <FormFieldXButton
                  onClick={() => {
                    setLastValidColorValue(null);
                    handleColorRemove?.();
                  }}
                />
              )
            );
          }}
        />
      </div>
    );
  }

  const _onSelectSavedStyle = (value: string) => {
    setToggleGroupValue("solid");
    onSelectSavedStyle?.(value);
  };

  const shouldOpenPopover = isDesignLibraryValue || isGradientColor(value);

  return (
    <div
      className={twMerge(
        "flex flex-grow flex-row justify-items-start gap-1",
        shouldOpenPopover && "cursor-pointer",
      )}
      onClick={() => {
        if (shouldOpenPopover) {
          setIsPopoverOpen(true);
        }
      }}
    >
      <DeprecatedInput
        unsafe_inputClassName={twMerge(shouldOpenPopover && "cursor-pointer")}
        value={inputValue}
        placeholder="#000000"
        onChange={(e) => {
          if (!isInputReadOnly) {
            setInputValue(e.target.value);
          }
        }}
        onKeyDown={(e) => e.stopPropagation()}
        onBlur={handleColorChange}
        startEnhancer={
          allowColorPickerPopover ? (
            <ColorPickerWrapper
              isPopoverOpen={isPopoverOpen}
              onOpenPopoverChange={(isOpen) => {
                setIsPopoverOpen(isOpen);
                if (isOpen) {
                  handleColorChange();
                }
              }}
              popoverTitle={popoverTitle}
              popoverSideOffset={popoverSideOffset}
              allowsGradientSelection
              onSelectSavedStyle={_onSelectSavedStyle}
              showSavedStyles={showSavedStyles}
              trigger={
                <Tooltip content={popoverTitle ?? ""} triggerAsChild>
                  <div
                    tabIndex={0}
                    // NOTE (Sebas, 2024-11-05): This is needed to prevent selecting the input when
                    // clicking on the color picker popover trigger. If we don't do this, the input
                    // will focus and unfocus triggering an onChange event because of the onBlur event.
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Badge
                      type={isMixedStyleValue(value) ? "unknown" : "color"}
                      isFilled
                      backgroundColor={badgeValue}
                    />
                  </div>
                </Tooltip>
              }
              content={
                <>
                  <DeprecatedToggleGroup
                    size="sm"
                    options={[
                      { label: "Solid", value: "solid" },
                      {
                        label: "Gradient",
                        value: "linear",
                      },
                    ]}
                    selectedValue={toggleGroupValue}
                    onChange={(toggleValue) => {
                      let color = null;
                      if (hasMixedStyleValue) {
                        color = "#000000";
                      } else {
                        color =
                          value?.type === "solid"
                            ? value.color
                            : value?.gradient?.stops[0]?.color;
                      }

                      const hexColor = getHex8Color(color ?? "#000000");

                      setToggleGroupValue(toggleValue);

                      if (toggleValue === "solid") {
                        handleChange({
                          type: "solid",
                          color: hexColor,
                        });
                      } else {
                        handleChange({
                          type: "gradient",
                          gradient: {
                            tilt: "0deg",
                            stops: [
                              {
                                id: uuidv4(),
                                color: hexColor,
                                location: "0%",
                              },
                            ],
                          },
                        });
                      }
                    }}
                  />
                  {toggleGroupValue === "solid" ? (
                    <SolidColorPicker
                      value={
                        !isMixedStyleValue(value)
                          ? (value as SolidValue)?.color ?? null
                          : value
                      }
                      onChange={(value) => {
                        if (value) {
                          handleChange({
                            type: "solid",
                            color: getFormattedColor(value),
                          });
                        }
                      }}
                      onPreviewChange={(value) => {
                        return onPreviewChange?.({
                          type: "solid",
                          color: value,
                        });
                      }}
                      onDragEnd={onDragEnd}
                      onDragStart={onDragStart}
                    />
                  ) : (
                    <GradientPicker
                      value={
                        !isMixedStyleValue(value)
                          ? (value as GradientValue)
                          : value
                      }
                      onChange={(value) => {
                        handleChange(value);
                      }}
                      onPreviewChange={(value) => onPreviewChange?.(value)}
                      onDragEnd={onDragEnd}
                      onDragStart={onDragStart}
                    />
                  )}
                </>
              }
            />
          ) : (
            <Badge type="color" isFilled backgroundColor={badgeValue} />
          )
        }
        onOptionClick={handleColorRemove}
        onEnter={handleColorChange}
        isReadOnly={isInputReadOnly}
        endEnhancer={() => {
          return (
            handleColorRemove &&
            valueText && (
              <FormFieldXButton
                onClick={() => {
                  setLastValidColorValue(null);
                  handleColorRemove?.();
                }}
              />
            )
          );
        }}
        autoFocus={false}
      />

      {dynamicData && (
        <div className="flex">
          <DynamicDataSelector
            side="left"
            sideOffset={90}
            targetType="text/color"
            trigger={<DynamicDataButton />}
            onChange={(value) => {
              onChange(value);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default GradientSelector;
